import { Component, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormFieldComponent } from '../../../../../../shared/components/form-field/form-field.component';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { LoggerService } from '../../../../../../core/logging/logger.service';

@Component({
  selector: 'app-entity-identification',
  standalone: true,
  imports: [CommonModule, FormFieldComponent, AzButtonComponent],
  templateUrl: './entity-identification.component.html',
  styleUrls: ['./entity-identification.component.scss'],
})
export class EntityIdentificationComponent implements OnChanges, OnInit {
  @Input() legalEntityId: number | null = null;
  @Input() legalEntityName = '';
  @Input() officialRegistrationNumber = '';
  @Input() associatedAuthorizedSignatory = '';
  @Input() address = '';
  @Input() country = '';

  isEditing = false;
  isSaving = false;

  // Store original values to detect changes
  private originalRegistrationNumber = '';
  private originalAuthorizedSignatory = '';

  // Current edited values
  currentRegistrationNumber = '';
  currentAuthorizedSignatory = '';

  constructor(
    private legalEntityService: LegalEntityService,
    public userPermissions: UserPermissionsService,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    // Initialize internal variables with Input values
    this.currentRegistrationNumber = this.officialRegistrationNumber;
    this.currentAuthorizedSignatory = this.associatedAuthorizedSignatory;
    this.originalRegistrationNumber = this.officialRegistrationNumber;
    this.originalAuthorizedSignatory = this.associatedAuthorizedSignatory;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update internal values when Input properties change (if not in edit mode)
    if (!this.isEditing) {
      if (changes['officialRegistrationNumber']) {
        this.currentRegistrationNumber = this.officialRegistrationNumber;
        this.originalRegistrationNumber = this.officialRegistrationNumber;
      }
      if (changes['associatedAuthorizedSignatory']) {
        this.currentAuthorizedSignatory = this.associatedAuthorizedSignatory;
        this.originalAuthorizedSignatory = this.associatedAuthorizedSignatory;
      }
    }

    // Reset edit mode when legalEntityId changes (user selected a different entity)
    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.isEditing = false;
      this.isSaving = false;
      this.currentRegistrationNumber = this.officialRegistrationNumber;
      this.currentAuthorizedSignatory = this.associatedAuthorizedSignatory;
      this.originalRegistrationNumber = this.officialRegistrationNumber;
      this.originalAuthorizedSignatory = this.associatedAuthorizedSignatory;
    }
  }

  get hasChanges(): boolean {
    return (
      this.currentRegistrationNumber !== this.originalRegistrationNumber ||
      this.currentAuthorizedSignatory !== this.originalAuthorizedSignatory
    );
  }

  get canEdit(): boolean {
    return this.userPermissions.hasEditEntityIdentificationPermission();
  }

  toggleEdit(): void {
    if (!this.isEditing) {
      // Entering edit mode - store current values as original (baseline for comparison)
      // Use current internal values, not Input properties, as they have the latest saved state
      this.originalRegistrationNumber = this.currentRegistrationNumber;
      this.originalAuthorizedSignatory = this.currentAuthorizedSignatory;
    }
    this.isEditing = !this.isEditing;
  }

  discard(): void {
    // Revert changes to original values
    this.currentRegistrationNumber = this.originalRegistrationNumber;
    this.currentAuthorizedSignatory = this.originalAuthorizedSignatory;
    this.isEditing = false;
  }

  saveChanges(): void {
    if (!this.legalEntityId || this.isSaving) {
      return;
    }

    this.isSaving = true;

    const payload = {
      registration_number: this.currentRegistrationNumber,
      signatories: this.currentAuthorizedSignatory,
    };

    this.legalEntityService.updateEntityIdentification(this.legalEntityId, payload).subscribe({
      next: (response) => {
        this.logger.info('Entity identification update response:', response);

        // Update all internal values with saved response
        // Use the response values, or keep current if response is null/undefined
        this.currentRegistrationNumber =
          response.registration_number ?? this.currentRegistrationNumber;
        this.currentAuthorizedSignatory = response.signatories ?? this.currentAuthorizedSignatory;
        this.originalRegistrationNumber =
          response.registration_number ?? this.currentRegistrationNumber;
        this.originalAuthorizedSignatory = response.signatories ?? this.currentAuthorizedSignatory;

        // Exit edit mode
        this.isEditing = false;
        this.isSaving = false;

        this.logger.info('Updated internal values:', {
          currentRegistrationNumber: this.currentRegistrationNumber,
          currentAuthorizedSignatory: this.currentAuthorizedSignatory,
        });
      },
      error: (error) => {
        this.logger.error('Error updating entity identification:', error);
        this.isSaving = false;
      },
    });
  }

  onRegistrationNumberChange(value: string): void {
    this.currentRegistrationNumber = value;
  }

  onAuthorizedSignatoryChange(value: string): void {
    this.currentAuthorizedSignatory = value;
  }
}
